import React, { useMemo, useState } from "react"
import { Pressable, View } from "react-native"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import moment from "moment"
import "moment/locale/fr"

import TextM from "@common/components/text/TextM"
import { theme } from "@common/theme/theme"

const WEEKDAYS = ["L", "M", "M", "J", "V", "S", "D"]

const stripTime = (d) =>
    moment(d).hours(0).minutes(0).seconds(0).milliseconds(0)

const MobileCalendar = ({ value, min, max, onChange }) => {
    const initialMonth = useMemo(
        () => stripTime(value || min || new Date()).startOf("month"),
        []
    )
    const [cursor, setCursor] = useState(initialMonth)

    const minM = min ? stripTime(min) : null
    const maxM = max ? stripTime(max) : null
    const valueM = value ? stripTime(value) : null

    const monthStart = cursor.clone().startOf("month")
    const monthEnd = cursor.clone().endOf("month")
    const daysInMonth = monthEnd.date()
    const firstWeekday = (monthStart.isoWeekday() + 6) % 7

    const cells = useMemo(() => {
        const arr = []
        for (let i = 0; i < firstWeekday; i++) arr.push(null)
        for (let d = 1; d <= daysInMonth; d++) {
            arr.push(monthStart.clone().date(d))
        }
        while (arr.length % 7 !== 0) arr.push(null)
        return arr
    }, [cursor, daysInMonth, firstWeekday])

    const canGoPrev = !minM || monthStart.isAfter(minM, "month")
    const canGoNext = !maxM || monthStart.isBefore(maxM, "month")

    const isDisabled = (day) =>
        (minM && day.isBefore(minM, "day")) ||
        (maxM && day.isAfter(maxM, "day"))

    const isSelected = (day) => valueM && day.isSame(valueM, "day")
    const isToday = (day) => day.isSame(moment(), "day")

    return (
        <View style={{ width: "100%" }}>
            <View
                style={{
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                    paddingHorizontal: 4,
                    marginBottom: 8,
                }}
            >
                <Pressable
                    onPress={() =>
                        canGoPrev &&
                        setCursor(cursor.clone().subtract(1, "month"))
                    }
                    disabled={!canGoPrev}
                    style={{
                        padding: 10,
                        opacity: canGoPrev ? 1 : 0.3,
                    }}
                    hitSlop={8}
                >
                    <FontAwesome5
                        name="chevron-left"
                        size={16}
                        color={theme.colors.primary}
                        solid
                    />
                </Pressable>
                <TextM fontWeight="Bold" fontSize={16}>
                    {cursor.locale("fr").format("MMMM YYYY")}
                </TextM>
                <Pressable
                    onPress={() =>
                        canGoNext && setCursor(cursor.clone().add(1, "month"))
                    }
                    disabled={!canGoNext}
                    style={{
                        padding: 10,
                        opacity: canGoNext ? 1 : 0.3,
                    }}
                    hitSlop={8}
                >
                    <FontAwesome5
                        name="chevron-right"
                        size={16}
                        color={theme.colors.primary}
                        solid
                    />
                </Pressable>
            </View>

            <View
                style={{
                    flexDirection: "row",
                    marginBottom: 4,
                }}
            >
                {WEEKDAYS.map((w, i) => (
                    <View
                        key={`${w}-${i}`}
                        style={{
                            flex: 1,
                            alignItems: "center",
                            paddingVertical: 6,
                        }}
                    >
                        <TextM
                            fontWeight="SemiBold"
                            fontSize={12}
                            color={theme.colors.grey}
                        >
                            {w}
                        </TextM>
                    </View>
                ))}
            </View>

            <View style={{ flexDirection: "row", flexWrap: "wrap" }}>
                {cells.map((day, idx) => {
                    if (!day) {
                        return (
                            <View
                                key={`empty-${idx}`}
                                style={{
                                    width: `${100 / 7}%`,
                                    aspectRatio: 1,
                                }}
                            />
                        )
                    }
                    const disabled = isDisabled(day)
                    const selected = isSelected(day)
                    const today = isToday(day)
                    return (
                        <Pressable
                            key={day.format("YYYY-MM-DD")}
                            onPress={() => !disabled && onChange(day.toDate())}
                            disabled={disabled}
                            style={{
                                width: `${100 / 7}%`,
                                aspectRatio: 1,
                                alignItems: "center",
                                justifyContent: "center",
                                padding: 2,
                            }}
                        >
                            <View
                                style={{
                                    width: "86%",
                                    aspectRatio: 1,
                                    borderRadius: 999,
                                    backgroundColor: selected
                                        ? theme.colors.primary
                                        : "transparent",
                                    borderWidth: !selected && today ? 1 : 0,
                                    borderColor: theme.colors.primary,
                                    alignItems: "center",
                                    justifyContent: "center",
                                    opacity: disabled ? 0.3 : 1,
                                }}
                            >
                                <TextM
                                    fontWeight={
                                        selected || today ? "Bold" : "Regular"
                                    }
                                    fontSize={14}
                                    color={
                                        selected
                                            ? theme.colors.white
                                            : theme.colors.dark
                                    }
                                >
                                    {day.date()}
                                </TextM>
                            </View>
                        </Pressable>
                    )
                })}
            </View>
        </View>
    )
}

export default MobileCalendar
